/**
 * HTTP-based Statement Execution Service
 * Uses unified compute-api SQL statements endpoints (Databricks-compatible)
 */
import {
  RATE_LIMIT_MAX_RETRIES,
  RATE_LIMIT_STATUS,
  resolveRetryDelayMs,
} from '@/lib/api/rateLimit';
import { handleSessionExpired, refreshToken } from '@/lib/api/tokenRefreshService';
import { API_CONFIG } from '@/config/api';
import { storeLogsInLocalStorage } from '../utils';

// Sentinel string baked into Error.message when rate-limit retries are exhausted.
// handleExecutionError detects this so it can strip the marker and surface the
// backend's user-facing error message verbatim to the UI.
export const RATE_LIMITED_ERROR_MARKER = '__RATE_LIMITED__';

// ---- IP-allowlist denials -------------------------------------------------
// Two independent surfaces deny on source IP, with different wire shapes, so
// each keeps its own detector:
//   1. compute-api's guard on POST /api/v1/system-catalog/execute — a JSON
//      body carrying a machine-readable code (the platform query engine).
//   2. the Envoy front door's http.rbac filter on the console statements path
//      — a plain-text body, emitted before the request ever reaches the app.

// The compute-api guard on POST /api/v1/system-catalog/execute denies with
// this machine-readable code when the caller's IP is off the workspace
// allowlist (the platform query engine rides the cluster rules).
export const PLATFORM_IP_ALLOWLIST_DENIED_CODE = 'IP_ALLOWLIST_DENIED';

// Deliberately avoids the word "cluster": useQueryExecution routes
// cluster-substring errors into a start-cluster quick action, which no
// allowlist change can fix.
export const PLATFORM_IP_ALLOWLIST_DENIED_MESSAGE =
  "Your IP address is not on the workspace's IP allowlist. Ask a workspace admin to add it on the IP Allowlist page.";

/** True when a 403 body carries the guard's allowlist-denial code. */
const isPlatformAllowlistDenial = (status: number, body: string): boolean => {
  if (status !== 403) return false;
  try {
    return JSON.parse(body)?.code === PLATFORM_IP_ALLOWLIST_DENIED_CODE;
  } catch {
    return false;
  }
};

// Sentinel for an Envoy IP-allowlist denial. The front door's http.rbac filter
// denies with the distinctive plain-text body "RBAC: access denied" —
// application-level 403s return the backend's JSON error shape — so a
// statements call carrying it was blocked by the workspace IP allowlist, not
// by app authorization.
export const IP_ALLOWLIST_DENIED_ERROR_MARKER = '__IP_ALLOWLIST_DENIED__';

// User-facing message for a front-door allowlist denial. useQueryExecution
// routes on this exact string (imported constant), so the wording is free to
// change here without flipping the editor into a different error UX branch.
export const IP_ALLOWLIST_DENIED_MESSAGE =
  "Your IP address is not on this cluster's IP allowlist. Ask a workspace admin to add it on the IP Allowlist page.";

// An Envoy-RBAC 403 is an allowlist denial; any other 403 keeps its existing
// (application-level) handling.
const isIpAllowlistDenial = (status: number, body: string): boolean =>
  status === 403 && body.includes('RBAC: access denied');

/**
 * Error for a non-OK HTTP response from a statement endpoint, carrying the status
 * code alongside the message.
 *
 * Previously every failure was thrown as a bare `Error` whose message merely
 * embedded the status as text, so `handleExecutionError` — which reads
 * `error.response.status` — always saw `undefined`, fell through to its default
 * branch, and stripped the caller's ability to tell a transient upstream blip
 * from a genuine failure. Retry logic keyed on status was therefore dead code.
 */
export class StatementApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = 'StatementApiError';
  }
}

/** Server-side statuses worth retrying: the request may never have reached the planner. */
const RETRYABLE_STATEMENT_STATUSES = [500, 502, 503, 504];

/**
 * True when the error is a transient server-side failure that a retry could clear.
 * Client errors (4xx) and terminal query states are never retryable.
 */
export const isRetryableStatementError = (error: unknown): boolean =>
  error instanceof StatementApiError && RETRYABLE_STATEMENT_STATUSES.includes(error.status);

const sleep = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Extract the user-facing error string from a non-OK Response. Tries to parse
 * the body as JSON and pull `error` or `message` (matches the backend's
 * `{ code, error }` shape); falls back to raw text, then to "Rate limited" if
 * the body is empty or unreadable.
 */
const readBackendErrorMessage = async (response: Response): Promise<string> => {
  let raw = '';
  try {
    raw = await response.text();
  } catch {
    return 'Rate limited';
  }
  if (!raw.trim()) return 'Rate limited';
  try {
    const body = JSON.parse(raw);
    if (typeof body?.error === 'string' && body.error.trim()) return body.error;
    if (typeof body?.message === 'string' && body.message.trim()) return body.message;
  } catch {
    // not JSON — fall through to raw text
  }
  return raw;
};

// Base URL for SQL statements API - uses shared config
const getBaseUrl = (): string => {
  // Use API_CONFIG.baseUrl (VITE_API_URL or defaults to http://localhost:8080)
  return API_CONFIG.baseUrl;
};

// Statement execution states
export const STATEMENT_STATES = {
  PENDING: 'PENDING',
  PLANNING: 'PLANNING',
  RUNNING: 'RUNNING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  CANCELED: 'CANCELED',
  TIMEDOUT: 'TIMEDOUT',
} as const;

export type StatementState = (typeof STATEMENT_STATES)[keyof typeof STATEMENT_STATES];

// Query execution statuses for UI compatibility
export const QUERY_STATUSES = {
  CONNECTING: 'Connecting with engine...',
  PLANNING: 'Planning...',
  EXECUTING: 'Executing...',
  FETCHING: 'Fetching results...',
  SUCCESS: 'Success',
  FAILED: 'Failed',
  CANCELED: 'Cancelled',
} as const;

export type QueryStatus = (typeof QUERY_STATUSES)[keyof typeof QUERY_STATUSES];

// Types
export interface ExecuteStatementParams {
  warehouse: string;
  catalog: string;
  schema: string;
  statement: string;
  parameters?: unknown[];
  clusterName?: string;
}

export interface StatementStatus {
  state: StatementState;
  error?: {
    message?: string;
    error_code?: string;
  };
}

export interface ColumnSchema {
  name: string;
  type_text?: string;
  type_name?: string;
  position?: number;
}

export interface Manifest {
  schema?: {
    columns?: ColumnSchema[];
  };
  total_chunk_count?: number;
  total_row_count?: number;
  truncated?: boolean;
}

export interface StatementResult {
  data_array?: unknown[][];
  row_count?: number;
}

export interface StatementResponse {
  statement_id: string;
  query_id?: string;
  status: StatementStatus;
  manifest?: Manifest;
  result?: StatementResult;
  session_id?: string;
}

export interface ChunkResponse {
  data_array?: unknown[][];
  chunk_index?: number;
  row_offset?: number;
  row_count?: number;
  next_chunk_index?: number;
  next_chunk_internal_link?: string;
  session_id?: string;
}

export interface ExecutionStatsResponse {
  explainAnalyze?: string;
  session_id?: string;
  [key: string]: unknown;
}

export interface ActiveStatement {
  statement_id: string;
  status: StatementStatus;
  /** Cluster the statement was submitted to — see clusterByStatement. */
  cluster: string;
  warehouse: string;
  catalog: string;
  schema: string;
  originalStatement: string;
}

export interface ParsedColumn {
  name: string;
  type: string | undefined;
  position: number;
  metadata: Record<string, unknown>;
}

export interface ParsedResultData {
  columns: ParsedColumn[];
  rows: unknown[][];
  hasMore: boolean;
  totalChunks?: number;
  truncated?: boolean;
}

export interface ProcessedChunks {
  rows: unknown[][];
  totalFetched: number;
  hasMore: boolean;
  truncated: boolean;
}

export interface PollingOptions {
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  maxPolls?: number;
}

export interface ParameterValue {
  type: string;
  value: unknown;
}

export interface FormattedParameters {
  [key: string]: ParameterValue;
}

type StatusUpdateCallback = (status: StatementResponse) => void | Promise<void>;

interface ApiError extends Error {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

/**
 * Statement Execution Service - v2 APIs
 * Uses unified compute-api endpoints with header-based authentication
 */
export class StatementExecutionService {
  private activeStatements: Map<string, ActiveStatement>;
  private pollingIntervals: Map<string, ReturnType<typeof setTimeout>>;
  // Default cluster for the NEXT submission only. Never use this to route a
  // follow-up call for an existing statement — see clusterByStatement.
  private currentClusterName: string | null;
  // Cluster each statement was submitted to, keyed by statementId.
  //
  // A statement lives on exactly one cluster: the planner that owns it is the one
  // that accepted the submit. Every follow-up call (status / chunks / cancel /
  // clearOrCancel / explain-analyze) must therefore route back to THAT cluster —
  // compute-api turns our `cluster` header into the `cluster-name` header that
  // Envoy routes on, and any other cluster's planner legitimately 404s because it
  // has never seen the statement.
  //
  // Before this map the cluster came from `currentClusterName`, a single global
  // read afresh on every request. Any second execution — another editor tab or a
  // same-tab re-run after switching the dropdown — overwrote it and silently
  // redirected the in-flight query's remaining polls to the wrong cluster: 404 from
  // that planner, surfaced to the editor as `502 UPSTREAM_ERROR`, while the query
  // itself ran to completion on the right cluster (RCA Q362901-12). Binding to the
  // statement makes that impossible.
  private clusterByStatement: Map<string, string>;
  // Session ID cached per cluster. Each cluster's planner mints and validates the
  // session JWT with a distinct key, so a session is only valid on the cluster that
  // issued it — sending one cluster's session to another is rejected (401).
  private sessionIdByCluster: Map<string, string>;
  private userEmail: string | null;

  constructor() {
    this.activeStatements = new Map(); // Track active statements
    this.pollingIntervals = new Map(); // Track polling intervals
    this.currentClusterName = null; // Default cluster for the next submission
    this.clusterByStatement = new Map(); // statementId -> owning cluster
    this.sessionIdByCluster = new Map(); // Session ID per cluster (sessions are cluster-scoped)
    this.userEmail = null; // User email for authentication
  }

  /**
   * Set the default cluster for the next submission (called from query execution flow).
   *
   * This does NOT affect statements that are already in flight — those are pinned to
   * the cluster they were submitted to via bindStatementCluster.
   */
  setClusterName(clusterName: string): void {
    this.currentClusterName = clusterName;
  }

  /**
   * Pin a statement to the cluster that owns it, so every follow-up call routes back
   * there regardless of what the rest of the app does afterwards.
   *
   * executeStatement does this automatically for every statement it submits.
   */
  bindStatementCluster(statementId: string, clusterName: string): void {
    if (!statementId) return;
    this.clusterByStatement.set(statementId, clusterName);
  }

  /**
   * Cluster to route a call for `statementId` to.
   *
   * Falls back to the current default only when the statement is unknown — a fresh
   * submit (no statementId yet) or the synthetic health-check id. A statement that
   * was bound at submit always resolves to its own cluster.
   */
  private resolveCluster(statementId?: string): string {
    if (statementId) {
      const bound = this.clusterByStatement.get(statementId);
      if (bound !== undefined) return bound;
    }
    return this.getClusterName() || '';
  }

  /**
   * Get the current cluster name
   */
  getClusterName(): string | null {
    return this.currentClusterName;
  }

  /**
   * Set user email for authentication
   */
  setUserEmail(email: string): void {
    this.userEmail = email;
  }

  /**
   * Get user email
   */
  getUserEmail(): string | null {
    return this.userEmail;
  }

  /**
   * Set session ID for reuse (skips re-authentication). Cached against the cluster
   * that issued it, since a session is only valid on the cluster whose planner
   * minted it.
   *
   * Callers pass the cluster the request actually used — not the current default,
   * which may have moved on since the request was sent and would file the session
   * under the wrong cluster.
   */
  setSessionId(sessionId: string, clusterName: string = this.getClusterName() || ''): void {
    this.sessionIdByCluster.set(clusterName, sessionId);
  }

  /**
   * Get common headers for API requests, targeting an explicit cluster.
   *
   * Always pass the cluster resolved for the statement being acted on; the default
   * is only correct for a fresh submit that has no statement yet.
   */
  getHeaders(clusterName: string = this.getClusterName() || ''): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Only send the cluster header when a cluster is actually selected. The
    // front door's allowlist gate matches all-clusters rules on header
    // PRESENCE, and Envoy counts a present-but-empty header as present — so
    // sending `cluster: ""` would get a no-cluster run denied as an allowlist
    // violation instead of reaching compute-api's clearer 400 "cluster header
    // is required".
    if (clusterName) {
      headers.cluster = clusterName;
    }

    // Include this cluster's session ID if available (skips re-authentication).
    // Keyed by the same cluster as the `cluster` header so we never send another
    // cluster's session — sessions are not portable across clusters.
    const sessionId = this.sessionIdByCluster.get(clusterName);
    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }

    return headers;
  }

  /**
   * Fetch wrapper that includes credentials and handles 401 + 429.
   *
   * - On 401: attempts a token refresh and retries the request once.
   * - On 429: retries up to RATE_LIMIT_MAX_RETRIES times honouring Retry-After
   *   (or capped exponential backoff when the header is absent). After exhaustion
   *   throws an Error tagged with RATE_LIMITED_ERROR_MARKER so handleExecutionError
   *   can surface a friendly message to the UI.
   * - On an Envoy IP-allowlist 403: throws an Error tagged with
   *   IP_ALLOWLIST_DENIED_ERROR_MARKER. Detected here rather than at each call
   *   site because the denial can land on ANY statements call — submit, poll,
   *   cancel, chunk fetch — and a per-site check silently misses new endpoints.
   *
   * All raw fetch() calls in this service should use this method.
   */
  private async fetchWithAuth(url: string, options: RequestInit): Promise<Response> {
    const fetchOptions: RequestInit = {
      ...options,
      credentials: 'include',
    };

    let response = await fetch(url, fetchOptions);

    if (response.status === 401) {
      const result = await refreshToken();
      if (result.success) {
        response = await fetch(url, fetchOptions);
      } else {
        handleSessionExpired(result.logoutUrl);
        return response;
      }
    }

    for (let attempt = 0; attempt < RATE_LIMIT_MAX_RETRIES; attempt++) {
      if (response.status !== RATE_LIMIT_STATUS) break;
      const delay = resolveRetryDelayMs(response.headers.get('Retry-After'), attempt);
      await sleep(delay);
      response = await fetch(url, fetchOptions);
    }

    if (response.status === RATE_LIMIT_STATUS) {
      // After retries are exhausted, extract the backend's user-facing message
      // from the 429 body (typically `{ "code": "RATE_LIMITED", "error": "..." }`)
      // so the editor surfaces the actual reason rather than a generic string.
      const backendMessage = await readBackendErrorMessage(response);
      throw new Error(`${RATE_LIMITED_ERROR_MARKER}: ${backendMessage}`);
    }

    // clone() so the caller's own `await response.text()` still sees a body.
    if (response.status === 403 && isIpAllowlistDenial(403, await response.clone().text())) {
      throw new Error(IP_ALLOWLIST_DENIED_ERROR_MARKER);
    }

    return response;
  }

  /**
   * 1. Execute SQL Statement
   * POST /api/v2/sql/statements
   */
  async executeStatement({
    warehouse,
    catalog,
    schema,
    statement,
    parameters = [],
    clusterName,
  }: ExecuteStatementParams): Promise<StatementResponse> {
    try {
      // Use cluster name from parameter or stored value. This is the one call that
      // legitimately reads the default: there is no statement to resolve from yet.
      const effectiveClusterName = clusterName || this.getClusterName() || '';
      if (clusterName) {
        this.setClusterName(clusterName);
      }

      // Build request payload
      const payload = {
        statement: statement,
        warehouse_id: warehouse,
        catalog: catalog,
        schema: schema,
        parameters: parameters,
        wait_timeout: '0s',
        on_wait_timeout: 'CONTINUE',
        format: 'JSON_ARRAY',
        row_limit: 10000,
      };

      storeLogsInLocalStorage({
        type: 'log',
        message: `[STATEMENT_LOG] Executing statement on cluster ${effectiveClusterName}: ${statement.substring(0, 100)}...`,
      });

      const baseUrl = getBaseUrl();
      const response = await this.fetchWithAuth(`${baseUrl}/api/v2/sql/statements`, {
        method: 'POST',
        headers: this.getHeaders(effectiveClusterName),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new StatementApiError(
          `Execute failed with status: ${response.status} - ${errorText}`,
          response.status
        );
      }

      const data: StatementResponse = await response.json();

      // Pin the statement to the cluster that accepted it, before anything else can
      // change the default. Every follow-up call now routes here on its own.
      this.bindStatementCluster(data.statement_id, effectiveClusterName);

      // Store session ID for subsequent requests (skips re-authentication)
      if (data.session_id) {
        this.setSessionId(data.session_id, effectiveClusterName);
      }

      // Store active statement
      this.activeStatements.set(data.statement_id, {
        statement_id: data.statement_id,
        status: data.status,
        cluster: effectiveClusterName,
        warehouse,
        catalog,
        schema,
        originalStatement: statement,
      });

      return data;
    } catch (error) {
      throw this.handleExecutionError(error as ApiError);
    }
  }

  /**
   * 2. Poll Statement Status
   * GET /api/v2/sql/statements/{statementId}
   */
  async getStatementStatus(statementId: string): Promise<StatementResponse> {
    try {
      const cluster = this.resolveCluster(statementId);
      const baseUrl = getBaseUrl();
      const response = await this.fetchWithAuth(`${baseUrl}/api/v2/sql/statements/${statementId}`, {
        method: 'GET',
        headers: this.getHeaders(cluster),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new StatementApiError(
          `Status check failed with status: ${response.status} - ${errorText}`,
          response.status
        );
      }

      const data: StatementResponse = await response.json();

      // Update session ID if returned
      if (data.session_id) {
        this.setSessionId(data.session_id, cluster);
      }

      return data;
    } catch (error) {
      throw this.handleExecutionError(error as ApiError);
    }
  }

  /**
   * 3. Cancel Statement Execution
   * POST /api/v2/sql/statements/{statementId}/cancel
   */
  async cancelStatement(statementId: string): Promise<StatementResponse> {
    try {
      this.stopPolling(statementId);

      const cluster = this.resolveCluster(statementId);
      const baseUrl = getBaseUrl();
      const response = await this.fetchWithAuth(
        `${baseUrl}/api/v2/sql/statements/${statementId}/cancel`,
        {
          method: 'POST',
          headers: this.getHeaders(cluster),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new StatementApiError(
          `Cancel failed with status: ${response.status} - ${errorText}`,
          response.status
        );
      }

      const data: StatementResponse = await response.json();

      storeLogsInLocalStorage({
        type: 'log',
        message: `[STATEMENT_LOG] Cancelled statement: ${statementId}`,
      });

      // Update session ID if returned
      if (data.session_id) {
        this.setSessionId(data.session_id, cluster);
      }

      return data;
    } catch (error) {
      throw this.handleExecutionError(error as ApiError);
    }
  }

  /**
   * 3b. Clear or Cancel Statement (HTTP v2 analog of JDBC clearOrCancelQuery)
   * POST /api/v2/sql/statements/{statementId}/clearOrCancel
   *
   * Call this when finished with a statement so planner + executor memory is freed
   * immediately instead of waiting for the server's 1-hour TTL sweeper. Idempotent —
   * calling again after it has already cleared is a no-op on the server (404-safe).
   *
   * Handles both running and terminal states: a running query is cancelled; a completed
   * query has its cached result + QueryContext released. Matches the gRPC semantics.
   */
  async clearOrCancelStatement(statementId: string): Promise<void> {
    try {
      this.stopPolling(statementId);

      const cluster = this.resolveCluster(statementId);
      const baseUrl = getBaseUrl();
      const response = await this.fetchWithAuth(
        `${baseUrl}/api/v2/sql/statements/${statementId}/clearOrCancel`,
        {
          method: 'POST',
          headers: this.getHeaders(cluster),
        }
      );

      // 404 is expected if the statement was already cleared / never existed — treat as success.
      if (!response.ok && response.status !== 404) {
        const errorText = await response.text();
        throw new StatementApiError(
          `clearOrCancel failed with status: ${response.status} - ${errorText}`,
          response.status
        );
      }

      storeLogsInLocalStorage({
        type: 'log',
        message: `[STATEMENT_LOG] clearOrCancel: ${statementId} (status ${response.status})`,
      });
    } catch (error) {
      // Don't surface cleanup errors to the user — cleanup is best-effort.
      storeLogsInLocalStorage({
        type: 'error',
        message: `[STATEMENT_LOG] clearOrCancel error for ${statementId}: ${(error as Error).message}`,
      });
    } finally {
      // End of the statement's life: it stopped polling above and callers issue no
      // further requests for it, so drop the binding rather than let the map grow
      // for the lifetime of the session. Runs on the error path too — a failed
      // cleanup call doesn't mean the statement is still in use.
      this.clusterByStatement.delete(statementId);
    }
  }

  /**
   * 4. Fetch Result Chunks
   * GET /api/v2/sql/statements/{statementId}/result/chunks/{chunkIndex}
   */
  async getResultChunk(statementId: string, chunkIndex: number): Promise<ChunkResponse> {
    try {
      const cluster = this.resolveCluster(statementId);
      const baseUrl = getBaseUrl();
      const response = await this.fetchWithAuth(
        `${baseUrl}/api/v2/sql/statements/${statementId}/result/chunks/${chunkIndex}`,
        {
          method: 'GET',
          headers: this.getHeaders(cluster),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new StatementApiError(
          `Chunk fetch failed with status: ${response.status} - ${errorText}`,
          response.status
        );
      }

      const data: ChunkResponse = await response.json();

      // Update session ID if returned
      if (data.session_id) {
        this.setSessionId(data.session_id, cluster);
      }

      return data;
    } catch (error) {
      throw this.handleExecutionError(error as ApiError);
    }
  }

  /**
   * 5. Get Execution Statistics (Explain/Analyze)
   * GET /api/v2/sql/statements/{statementId}/explain-analyze
   */
  async getExecutionStats(statementId: string): Promise<ExecutionStatsResponse | null> {
    try {
      const cluster = this.resolveCluster(statementId);
      const baseUrl = getBaseUrl();
      const response = await this.fetchWithAuth(
        `${baseUrl}/api/v2/sql/statements/${statementId}/explain-analyze`,
        {
          method: 'GET',
          headers: this.getHeaders(cluster),
        }
      );

      if (!response.ok) {
        // explain-analyze is non-critical, don't fail
        return null;
      }

      const data: ExecutionStatsResponse = await response.json();

      // Update session ID if returned
      if (data.session_id) {
        this.setSessionId(data.session_id, cluster);
      }

      return data;
    } catch {
      return null;
    }
  }

  /**
   * Poll statement until completion with configurable delays
   */
  async pollUntilComplete(
    statementId: string,
    onStatusUpdate: StatusUpdateCallback | null,
    {
      initialDelay = 1000,
      maxDelay = 5000,
      backoffMultiplier = 1.5,
      maxPolls = 300, // 5 minutes max with 1s intervals
    }: PollingOptions = {}
  ): Promise<StatementResponse> {
    return new Promise((resolve, reject) => {
      let pollCount = 0;
      let currentDelay = initialDelay;

      const poll = async (): Promise<void> => {
        try {
          if (pollCount >= maxPolls) {
            reject(new Error('Polling timeout: Statement took too long to complete'));
            return;
          }

          const status = await this.getStatementStatus(statementId);
          pollCount++;

          // Call status update callback
          if (onStatusUpdate) {
            await onStatusUpdate(status);
          }

          // Check if statement is in terminal state
          const terminalStates: StatementState[] = [
            STATEMENT_STATES.SUCCEEDED,
            STATEMENT_STATES.FAILED,
            STATEMENT_STATES.CANCELED,
          ];
          const isComplete = terminalStates.includes(status.status?.state as StatementState);

          if (isComplete) {
            this.stopPolling(statementId);
            resolve(status);
            return;
          }

          // Continue polling with backoff
          currentDelay = Math.min(currentDelay * backoffMultiplier, maxDelay);
          const timeoutId = setTimeout(poll, currentDelay);
          this.pollingIntervals.set(statementId, timeoutId);
        } catch (error) {
          this.stopPolling(statementId);
          reject(error);
        }
      };

      // Start initial poll
      const timeoutId = setTimeout(poll, 0);
      this.pollingIntervals.set(statementId, timeoutId);
    });
  }

  /**
   * Stop polling for a statement
   */
  stopPolling(statementId: string): void {
    const timeoutId = this.pollingIntervals.get(statementId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.pollingIntervals.delete(statementId);
    }
  }

  /**
   * Fetch all result chunks and combine into rows
   */
  async fetchAllChunks(statementId: string, manifest: Manifest): Promise<unknown[][]> {
    const { total_chunk_count } = manifest;
    if (!total_chunk_count || total_chunk_count <= 1) {
      return []; // No additional chunks
    }

    const allChunkRows: unknown[][] = [];
    for (let chunkIndex = 1; chunkIndex < total_chunk_count; chunkIndex++) {
      const chunkData = await this.getResultChunk(statementId, chunkIndex);
      if (chunkData.data_array) {
        allChunkRows.push(...chunkData.data_array);
      }
    }

    return allChunkRows;
  }

  /**
   * Process and combine result chunks
   */
  async processResultChunks(
    statementId: string,
    initialResult: StatementResult,
    manifest: Manifest
  ): Promise<ProcessedChunks> {
    const allRows: unknown[][] = [...(initialResult.data_array || [])];

    if (manifest.total_chunk_count && manifest.total_chunk_count > 1) {
      const additionalRows = await this.fetchAllChunks(statementId, manifest);
      allRows.push(...additionalRows);
    }

    // Apply row limit (maintain compatibility with existing 10k limit)
    const limitedRows = allRows.slice(0, 10000);

    return {
      rows: limitedRows,
      totalFetched: limitedRows.length,
      hasMore: allRows.length > 10000,
      truncated: manifest.truncated || allRows.length > 10000,
    };
  }

  /**
   * Parse result data from statement response
   */
  parseResultData(statementResponse: StatementResponse): ParsedResultData {
    const { result, manifest } = statementResponse;

    if (!result || !manifest) {
      return { columns: [], rows: [], hasMore: false };
    }

    // Parse schema into column metadata
    const columns: ParsedColumn[] = manifest.schema?.columns
      ? manifest.schema.columns.map((col, index) => ({
          name: col.name,
          type: col.type_text || col.type_name,
          position: col.position ?? index,
          metadata: {},
        }))
      : [];

    // Parse result data
    const rows = result.data_array || [];
    const hasMore = (manifest.total_chunk_count || 0) > 1;

    return {
      columns,
      rows,
      hasMore,
      totalChunks: manifest.total_chunk_count,
      truncated: manifest.truncated || false,
    };
  }

  /**
   * Handle execution errors with proper mapping
   */
  handleExecutionError(error: ApiError): Error {
    const status = error?.response?.status;
    const message = error?.response?.data?.message || error.message;

    // Status carried by our own fetch errors. Deliberately NOT merged into `status`
    // above: `status` selects the message below, and callers branch on that message
    // text — a 404 whose body reads "cluster 'x' not found" drives the cluster
    // quick-action + cluster-list refresh in useQueryExecution, and only reaches it
    // because the default branch passes the upstream body through verbatim. Folding
    // the fetch status into the switch would replace those bodies with generic
    // strings and silently disable that UX. All we need is for the status to survive
    // on the returned error, so the poll loop can tell a transient upstream blip
    // from a genuine failure; the messages stay exactly as they are today.
    const upstreamStatus =
      status ?? (error instanceof StatementApiError ? error.status : undefined);

    const mapped = (text: string): Error =>
      upstreamStatus === undefined ? new Error(text) : new StatementApiError(text, upstreamStatus);

    // A platform-guard allowlist denial surfaces exactly as-is — wrapping it
    // in "Query execution failed:" would bury the actionable part.
    if (message === PLATFORM_IP_ALLOWLIST_DENIED_MESSAGE) {
      return new Error(PLATFORM_IP_ALLOWLIST_DENIED_MESSAGE);
    }
    // A front-door (Envoy RBAC) allowlist denial surfaces as its own
    // user-facing message — useQueryExecution routes on this exact string.
    if (message && message.includes(IP_ALLOWLIST_DENIED_ERROR_MARKER)) {
      return new Error(IP_ALLOWLIST_DENIED_MESSAGE);
    }
    // Rate-limit errors surface verbatim using the backend's own message,
    // which fetchWithAuth already extracted into the marker-prefixed string.
    if (message && message.includes(RATE_LIMITED_ERROR_MARKER)) {
      const cleaned = message.split(`${RATE_LIMITED_ERROR_MARKER}: `)[1] || message;
      return mapped(cleaned);
    }
    if (status === RATE_LIMIT_STATUS) {
      return mapped(message || 'Rate limited');
    }

    switch (status) {
      case 400:
        return mapped(`Invalid request: ${message}`);
      case 401:
        return mapped('Unauthorized: Please check your authentication');
      case 403:
        return mapped('Forbidden: User has no access token configured');
      case 404:
        return mapped('Statement not found or expired');
      case 502:
        return mapped('Upstream error from query planner');
      case 503:
        return mapped('Query service unavailable');
      default:
        return mapped(`Query execution failed: ${message}`);
    }
  }

  /**
   * 6. Execute System Catalog Statement (DuckDB-backed)
   * POST /api/v1/system-catalog/execute
   * Synchronous — no polling, no session management, no cluster required.
   */
  async executeSystemStatement(params: {
    statement: string;
    catalog: string;
    schema: string;
    rowLimit?: number;
  }): Promise<StatementResponse> {
    try {
      const baseUrl = getBaseUrl();
      const response = await this.fetchWithAuth(`${baseUrl}/api/v1/system-catalog/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          statement: params.statement,
          catalog: params.catalog,
          schema: params.schema,
          row_limit: params.rowLimit || 50000,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (isPlatformAllowlistDenial(response.status, errorText)) {
          throw new Error(PLATFORM_IP_ALLOWLIST_DENIED_MESSAGE);
        }
        throw new Error(
          `System catalog query failed with status: ${response.status} - ${errorText}`
        );
      }

      const data: StatementResponse = await response.json();
      return data;
    } catch (error) {
      throw this.handleExecutionError(error as ApiError);
    }
  }

  /**
   * Clean up resources for a statement
   */
  cleanup(statementId: string): void {
    this.stopPolling(statementId);
    this.activeStatements.delete(statementId);
    this.clusterByStatement.delete(statementId);
  }

  /**
   * Clean up all resources
   */
  cleanupAll(): void {
    // Stop all polling
    for (const [statementId] of this.pollingIntervals) {
      this.stopPolling(statementId);
    }

    // Clear all active statements
    this.activeStatements.clear();

    // Drop statement→cluster bindings
    this.clusterByStatement.clear();

    // Clear sessions for all clusters
    this.sessionIdByCluster.clear();
  }
}

// Export singleton instance
export const statementExecutionService = new StatementExecutionService();

/**
 * Statement execution parameter types
 */
export const PARAMETER_TYPES = {
  STRING: 'STRING',
  INT: 'INT',
  BIGINT: 'BIGINT',
  DOUBLE: 'DOUBLE',
  BOOLEAN: 'BOOLEAN',
  DATE: 'DATE',
  TIMESTAMP: 'TIMESTAMP',
} as const;

export type ParameterType = (typeof PARAMETER_TYPES)[keyof typeof PARAMETER_TYPES];

/**
 * Helper to convert parameters to proper types
 */
export const formatParameters = (
  params: Record<string, unknown> | null | undefined
): FormattedParameters => {
  const formatted: FormattedParameters = {};

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value && typeof value === 'object' && 'type' in value && 'value' in value) {
      formatted[key] = {
        type: (value as ParameterValue).type,
        value: (value as ParameterValue).value,
      };
    } else {
      // Auto-detect type for simple values
      formatted[key] = {
        type:
          typeof value === 'number'
            ? Number.isInteger(value)
              ? PARAMETER_TYPES.INT
              : PARAMETER_TYPES.DOUBLE
            : PARAMETER_TYPES.STRING,
        value: value,
      };
    }
  });

  return formatted;
};

// Legacy exports for compatibility
export class AuthenticationService {
  static bearerToken: string | null = null;

  static async authenticate(_clusterName?: string): Promise<boolean> {
    // Authentication is now handled via headers (X-User-Email, cluster-name)
    // No explicit authentication step needed
    return true;
  }

  static getBearerToken(): string | null {
    return this.bearerToken;
  }

  static clearToken(): void {
    this.bearerToken = null;
  }
}

export default statementExecutionService;
